import { useMemo, useState } from "react"
import _ from "lodash"

//Formik
import { useField } from "formik"

//PrimeReact
import { Dialog } from "primereact/dialog"
import { InputText } from "primereact/inputtext"
import { Checkbox } from "primereact/checkbox"
import { Button } from "primereact/button"

//Styles
import myStyles from "../../../../styles/kiosk/KioskAdminFormWizard.module.css"
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"

export default function SpecialFieldsDialog({
  onCancel,
  onAddSpecialFields,
  selectApplication,
}) {
  const intakeSessionName = "intake_session"
  const [field] = useField("fields")
  const [templateName] = useField("soarTemplateName")
  const [doApplyToSubject, setDoApplyToSubject] = useState(false)
  const [doApplyToBody, setDoApplyToBody] = useState(true)

  const kioskLocationField = {
    blockId: "__kioskLocation",
    blockName: "Kiosk Location",
    fieldId: "kioskLocation",
    fieldLabel: "Kiosk Location",
  }

  const participantAliasField = {
    blockId: "__newParticipantProfile",
    blockName: "New participant profile",
    fieldId: "participantAlias",
    fieldLabel: "Participant ID",
  }

  //Memoise useFields to prevent unnecessary rerenders if field.value doesn't change
  const availableFields = useMemo(() => {
    let fieldList = field.value?.filter((field) => field.show)

    fieldList = [kioskLocationField, ...fieldList]

    //If this is an intake template, add the special participant alias tag
    if (templateName.value === intakeSessionName) {
      return [participantAliasField, ...fieldList]
    } else {
      return fieldList
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value, templateName.value])

  const [searchText, setSearchText] = useState("")
  const [filteredFields, setFilteredFields] = useState(availableFields)
  const [addFields, setAddFields] = useState([])

  const RenderField = ({ fieldName, fieldId, blockId }) => {
    const processField = () => {
      const specField = `{{${blockId}.${fieldId}}}`

      if (
        !addFields.some(
          (addField) =>
            addField.fieldId === fieldId && addField.blockId === blockId,
        )
      ) {
        setAddFields([...addFields, { specField, fieldId, blockId }])
      } else {
        setAddFields(
          addFields.filter((addField) => addField.specField !== specField),
        )
      }
    }

    return (
      <div className={myStyles.specialField}>
        <Checkbox
          checked={addFields.some(
            (addField) =>
              addField.fieldId === fieldId && addField.blockId === blockId,
          )}
          onChange={(e) => {
            processField()
          }}
        />
        <span
          onClick={() => {
            processField()
          }}
        >
          {" "}
          {fieldName}
        </span>
      </div>
    )
  }

  const RenderBlock = ({ blockName, blockId }) => {
    let renderField = <></>
    const fields = filteredFields
      ?.filter((field) => field.blockName === blockName && field.fieldLabel)
      .sort((a, b) => (a.fieldLabel > b.fieldLabel ? 1 : -1))

    if (fields.length > 0) {
      renderField = (
        <div className={myStyles.specialBlock}>
          <div className={myStyles.specialBlockName}>{blockName}</div>
          <div className={myStyles.specialFieldContainer}>
            {fields.map((fieldEntry) => {
              return (
                <RenderField
                  fieldName={fieldEntry.fieldLabel}
                  fieldId={fieldEntry.fieldId}
                  blockId={blockId}
                />
              )
            })}
          </div>
        </div>
      )
    }
    return renderField
  }

  const RenderBlocks = () => {
    const blocks = _.uniqBy(filteredFields, "blockName").sort((a, b) =>
      a.blockName > b.blockName ? 1 : -1,
    )
    return (
      <div className={myStyles.specialBlockContainer}>
        {blocks.map((block) => {
          return (
            <RenderBlock blockName={block.blockName} blockId={block.blockId} />
          )
        })}
      </div>
    )
  }

  const submitSpecialFields = () => {
    let specialFields = addFields
      .map((specField) => specField.specField)
      .join("")

    onAddSpecialFields({
      specialFields,
      applyToSubject: doApplyToSubject,
      applyToBody: doApplyToBody,
    })
  }

  return (
    <Dialog
      visible={true}
      className={`${myStyles.dialog} ${myStyles.dialogSpecialField}`}
      showHeader={false}
    >
      <div className={kioskStyles.modalHeader}>
        Insert special fields
        <Button
          icon="pi pi-times"
          className={kioskStyles.hideModalButton}
          onClick={() => {
            onCancel()
          }}
        />
      </div>
      <div className={kioskStyles.modalFormContainer}>
        <hr className={kioskStyles.modalHeaderLine} />
        <div className={myStyles.specialFieldTop}>
          {selectApplication && (
            <div className={myStyles.applyTo}>
              Apply to:{" "}
              <div className="flex align-items-center">
                <Checkbox
                  inputId="Subject"
                  onChange={(change) => {
                    setDoApplyToSubject(change.checked)
                  }}
                  checked={doApplyToSubject}
                />
                <label htmlFor="Subject" className={myStyles.applyToText}>
                  Subject
                </label>
              </div>
              <div className="flex align-items-center">
                <Checkbox
                  inputId="Body"
                  onChange={(change) => {
                    setDoApplyToBody(change.checked)
                  }}
                  checked={doApplyToBody}
                />
                <label htmlFor="Body" className={myStyles.applyToText}>
                  Body
                </label>
              </div>
            </div>
          )}
          <div>
            <span
              className={`p-input-icon-right ${myStyles.specialFieldSearchSpan}`}
            >
              <i className="pi pi-search " />
              <InputText
                value={searchText}
                placeholder="Search"
                className={myStyles.specialFieldSearchInput}
                onChange={(e) => {
                  const searchValue = e.target.value
                  setSearchText(searchValue)

                  const lowercasedSearch = searchValue.toLowerCase()
                  const newFilteredFields = availableFields?.filter(
                    (fieldEntry) =>
                      fieldEntry.fieldLabel
                        ?.toLowerCase()
                        .includes(lowercasedSearch) ||
                      fieldEntry.fieldId
                        ?.toLowerCase()
                        .includes(lowercasedSearch),
                  )
                  setFilteredFields(newFilteredFields)
                }}
              />
            </span>
            <div className={myStyles.specialFieldSelectedContainer}>
              {addFields.map((addField) => {
                return (
                  <div
                    key={`${addField.blockId}.${addField.fieldId}`}
                    className={myStyles.specialFieldSelected}
                  >{`<${addField.blockId}.${addField.fieldId}>`}</div>
                )
              })}
            </div>
          </div>
        </div>

        <RenderBlocks />
        <div className={myStyles.specialFieldBottom}>
          <Button
            className={`${kioskStyles.invertButton} ${kioskStyles.button}`}
            label="Cancel"
            onClick={onCancel}
            type="button"
          />
          <Button
            className={`${kioskStyles.primaryButton} ${kioskStyles.button}`}
            label="Insert"
            type="button"
            disabled={addFields.length === 0}
            onClick={() => {
              submitSpecialFields()
            }}
          />
        </div>
      </div>
    </Dialog>
  )
}
